import React, { useMemo, useState } from 'react';

const SITE_URL = 'https://therapylist.com';

const LOGO_URL = 'https://cms.therapylist.com/wp-content/uploads/2025/07/1-scaled.png';

const SIZE_OPTIONS = [
  { id: 'small', label: 'Small', height: 120 },
  { id: 'medium', label: 'Medium', height: 180 },
  { id: 'large', label: 'Large', height: 240 },
];

const BG_PRESETS = [
  { id: 'none', label: 'None', color: 'transparent' },
  { id: 'white', label: 'White', color: '#ffffff' },
  { id: 'custom', label: 'Custom', color: null },
];

function normalizeHex(input) {
  if (!input) return null;
  let v = input.trim();
  if (!v) return null;
  if (!v.startsWith('#')) v = '#' + v;
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v) ? v : null;
}

function buildEmbedHtml({ profileUrl, height, bgColor }) {
  const hasBg = bgColor && bgColor !== 'transparent';
  const wrapperStyle = [
    'display:inline-block',
    'text-decoration:none',
    'border:0',
    'line-height:0',
    hasBg ? `background:${bgColor}` : null,
    hasBg ? 'padding:12px 16px' : null,
    hasBg ? 'border-radius:8px' : null,
  ]
    .filter(Boolean)
    .join(';');

  const imgStyle = [
    `height:${height}px`,
    'width:auto',
    'border:0',
    'display:block',
  ].join(';');

  return `<a href="${profileUrl}" target="_blank" rel="noopener" title="View my profile on TherapyList" style="${wrapperStyle};">
  <img src="${LOGO_URL}" alt="View my profile on TherapyList" style="${imgStyle};" />
</a>`;
}

function EmbedLogoCard({ slug }) {
  const [size, setSize] = useState('large');
  const [bgPreset, setBgPreset] = useState('none');
  const [customBg, setCustomBg] = useState('#ffffff');
  const [previewBgInput, setPreviewBgInput] = useState('#e4fde1');
  const [copied, setCopied] = useState(false);

  const selectedSize = SIZE_OPTIONS.find((s) => s.id === size) || SIZE_OPTIONS[1];
  const selectedBgPreset = BG_PRESETS.find((b) => b.id === bgPreset) || BG_PRESETS[0];
  const customHex = normalizeHex(customBg);
  const bgColor =
    bgPreset === 'custom' ? customHex || 'transparent' : selectedBgPreset.color;
  const hasBg = bgColor && bgColor !== 'transparent';

  const profileUrl = `${SITE_URL}/therapist/${slug}?ref=embed`;
  const embedHtml = useMemo(
    () =>
      buildEmbedHtml({
        profileUrl,
        height: selectedSize.height,
        bgColor,
      }),
    [profileUrl, selectedSize.height, bgColor]
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(embedHtml);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      const textarea = document.getElementById('embed-code-textarea');
      if (textarea) {
        textarea.select();
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  if (!slug) return null;

  const previewBg = normalizeHex(previewBgInput) || '#ffffff';

  const optionButtonClass = (active) =>
    `px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 border ${
      active
        ? 'bg-[#254e70] text-white border-[#254e70] shadow-sm'
        : 'bg-white text-[#254e70] border-[#254e70]/20 hover:bg-[#e4fde1]'
    }`;

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm border border-[#254e70]/10">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-12 h-12 bg-[#254e70] rounded-lg flex items-center justify-center">
          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" />
          </svg>
        </div>
        <div>
          <h3 className="text-xl font-medium text-[#254e70]" style={{ fontFamily: 'Kanit' }}>
            Embed on Your Website
          </h3>
          <p className="text-sm text-gray-600" style={{ fontFamily: 'Nunito Sans' }}>
            Add a TherapyList badge to your own site that links visitors directly to your profile.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Preview */}
        <div>
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <h4 className="text-base font-semibold text-[#254e70]" style={{ fontFamily: 'Kanit' }}>
              Preview
            </h4>
            <div className="flex items-center gap-2">
              <label
                className="text-xs text-[#254e70]"
                style={{ fontFamily: 'Nunito Sans' }}
                htmlFor="preview-bg-input"
              >
                Backdrop
              </label>
              <input
                id="preview-bg-input"
                type="text"
                value={previewBgInput}
                onChange={(e) => setPreviewBgInput(e.target.value)}
                placeholder="#ffffff"
                className="px-2 py-1 rounded-md text-xs border border-[#254e70]/20 focus:ring-2 focus:ring-[#9dacff] focus:border-[#254e70] focus:outline-none w-24 font-mono"
                style={{ fontFamily: 'Nunito Sans' }}
                spellCheck={false}
                aria-label="Preview backdrop hex color"
              />
              <span
                className="inline-block w-5 h-5 rounded border border-[#254e70]/20"
                style={{ background: previewBg }}
                aria-hidden="true"
              />
            </div>
          </div>
          <div
            className="flex items-center justify-center p-6 rounded-lg border border-[#254e70]/10 min-h-[180px] transition-colors"
            style={{ background: previewBg }}
          >
            <a
              href={profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              title="View my profile on TherapyList"
              style={{
                display: 'inline-block',
                textDecoration: 'none',
                border: 0,
                lineHeight: 0,
                background: hasBg ? bgColor : 'transparent',
                padding: hasBg ? '12px 16px' : 0,
                borderRadius: hasBg ? 8 : 0,
              }}
            >
              <img
                src={LOGO_URL}
                alt="View my profile on TherapyList"
                style={{
                  height: `${selectedSize.height}px`,
                  width: 'auto',
                  border: 0,
                  display: 'block',
                }}
              />
            </a>
          </div>
        </div>

        {/* Controls + code */}
        <div>
          {/* Size */}
          <div>
            <label
              className="block text-sm font-medium text-[#254e70] mb-2"
              style={{ fontFamily: 'Nunito Sans' }}
            >
              Size
            </label>
            <div className="flex flex-wrap gap-2">
              {SIZE_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setSize(opt.id)}
                  className={optionButtonClass(size === opt.id)}
                  style={{ fontFamily: 'Nunito Sans' }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Background */}
          <div className="mt-4">
            <label
              className="block text-sm font-medium text-[#254e70] mb-2"
              style={{ fontFamily: 'Nunito Sans' }}
            >
              Background
            </label>
            <p className="text-xs text-gray-600 mb-2" style={{ fontFamily: 'Nunito Sans' }}>
              A white card keeps the badge readable on any site color. Pick <em>None</em> only if your site is light/white.
            </p>
            <div className="flex flex-wrap gap-2 items-center">
              {BG_PRESETS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setBgPreset(opt.id)}
                  className={optionButtonClass(bgPreset === opt.id)}
                  style={{ fontFamily: 'Nunito Sans' }}
                >
                  {opt.label}
                </button>
              ))}
              {bgPreset === 'custom' && (
                <>
                  <input
                    type="text"
                    value={customBg}
                    onChange={(e) => setCustomBg(e.target.value)}
                    placeholder="#ffffff"
                    className="px-3 py-2 rounded-lg text-sm border border-[#254e70]/20 focus:ring-2 focus:ring-[#9dacff] focus:border-[#254e70] focus:outline-none w-28 font-mono"
                    style={{ fontFamily: 'Nunito Sans' }}
                    spellCheck={false}
                  />
                  {customHex && (
                    <span
                      className="inline-block w-6 h-6 rounded border border-[#254e70]/20"
                      style={{ background: customHex }}
                      aria-label="Custom color preview"
                    />
                  )}
                </>
              )}
            </div>
            {bgPreset === 'custom' && !customHex && (
              <p
                className="text-xs text-red-600 mt-1"
                style={{ fontFamily: 'Nunito Sans' }}
              >
                Enter a valid hex color (e.g. #1a2433).
              </p>
            )}
          </div>

          {/* Code + copy button */}
          <div className="mt-6 flex items-center justify-between mb-3">
            <h4 className="text-base font-semibold text-[#254e70]" style={{ fontFamily: 'Kanit' }}>
              Embed Code
            </h4>
            <button
              type="button"
              onClick={handleCopy}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 shadow-sm ${
                copied
                  ? 'bg-green-600 text-white'
                  : 'bg-[#9dacff] text-white hover:bg-[#8a9aff]'
              }`}
              style={{ fontFamily: 'Nunito Sans' }}
              aria-live="polite"
            >
              {copied ? (
                <>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Copied!
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M8 2a1 1 0 000 2h2a1 1 0 100-2H8z" />
                    <path d="M3 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v6h-4.586l1.293-1.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L10.414 13H15v3a2 2 0 01-2 2H5a2 2 0 01-2-2V5z" />
                    <path d="M15 11h2a1 1 0 110 2h-2v-2z" />
                  </svg>
                  Copy Code
                </>
              )}
            </button>
          </div>
          <textarea
            id="embed-code-textarea"
            readOnly
            value={embedHtml}
            onClick={(e) => e.target.select()}
            rows={8}
            className="w-full p-3 border border-[#254e70]/20 rounded-lg font-mono text-xs text-gray-800 bg-gray-50 focus:ring-2 focus:ring-[#9dacff] focus:border-[#254e70] transition-all duration-200 resize-none"
            spellCheck={false}
          />
          <p
            className="text-xs text-gray-600 mt-2"
            style={{ fontFamily: 'Nunito Sans' }}
          >
            Paste this HTML into your website where you want the badge to appear. Works with most site builders including WordPress, Squarespace, Wix, and Webflow.
          </p>
        </div>
      </div>
    </div>
  );
}

export default EmbedLogoCard;
